import React from 'react';
import type { CaseSreInvestigationStatus } from '../types';
import {
  getSreStatusPresentation,
  SRE_STATUS_TONE_STYLES,
} from '../utils/sreStatusMessaging';
import { formatLocalDateTime } from '../utils/formatLocalDateTime';

export interface SreCaseStatusBadgeProps {
  status?: CaseSreInvestigationStatus | string | null;
  reason?: string | null;
  evaluatedAt?: string | null;
  className?: string;
  /** When false, omit the pulse animation on live statuses. */
  animate?: boolean;
}

function StatusIcon({
  status,
  className = '',
}: {
  status: string;
  className?: string;
}) {
  const common = ['h-3.5 w-3.5 shrink-0', className].join(' ');

  if (status === 'completed') {
    return (
      <svg viewBox="0 0 12 12" className={common} fill="none" aria-hidden>
        <circle cx="6" cy="6" r="5" className="fill-current opacity-20" />
        <path
          d="M3.2 6.2 5.1 8 8.8 4"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (status === 'failed') {
    return (
      <svg viewBox="0 0 12 12" className={common} fill="none" aria-hidden>
        <circle cx="6" cy="6" r="5" className="fill-current opacity-20" />
        <path
          d="M4 4l4 4M8 4l-4 4"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (status === 'not_available' || status === 'skipped' || status === 'cancelled') {
    return (
      <svg viewBox="0 0 12 12" className={common} fill="none" aria-hidden>
        <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.4" />
        <path
          d="M3.5 6h5"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (status === 'queued' || status === 'pending_eval') {
    return (
      <svg viewBox="0 0 12 12" className={common} fill="none" aria-hidden>
        <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.4" />
        <path
          d="M6 3.5V6l1.8 1.2"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  // running / default
  return (
    <svg viewBox="0 0 12 12" className={common} fill="none" aria-hidden>
      <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="6" cy="6" r="2" className="fill-current" />
    </svg>
  );
}

/**
 * Ticket SRE status badge (T-022 / T-030).
 * When the gate/run outcome has operator-facing copy (T-031), hovering or
 * focusing the badge reveals it in a tooltip — no separate affordance needed.
 */
const SreCaseStatusBadge: React.FC<SreCaseStatusBadgeProps> = ({
  status,
  reason,
  evaluatedAt,
  className = '',
  animate = true,
}) => {
  const evaluatedLabel = evaluatedAt
    ? formatLocalDateTime(evaluatedAt, evaluatedAt)
    : null;
  const presentation = getSreStatusPresentation({
    status,
    reason,
    evaluatedAt: evaluatedLabel,
  });

  if (!presentation) {
    return null;
  }

  const tone = SRE_STATUS_TONE_STYLES[presentation.tone];
  const hasMessage = presentation.showMessage && Boolean(presentation.message);

  return (
    <span
      className={['group relative inline-flex', hasMessage ? '' : className].join(' ')}
    >
      <span
        tabIndex={hasMessage ? 0 : undefined}
        className={[
          'inline-flex items-center gap-1.5 rounded-lg border',
          'px-2.5 py-1.5 text-xs font-semibold',
          tone.badge,
          hasMessage ? className : '',
          hasMessage ? 'cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1' : '',
        ].join(' ')}
        title={hasMessage ? undefined : presentation.detail}
        aria-label={`SRE investigation status: ${presentation.label}`}
        data-sre-case-status={presentation.status}
      >
        <span
          className={[
            'relative flex h-3.5 w-3.5 items-center justify-center',
            animate && presentation.pulse ? 'animate-pulse' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-hidden
        >
          <StatusIcon status={presentation.status} />
        </span>
        <span>{presentation.label}</span>
      </span>

      {hasMessage ? (
        <span
          role="tooltip"
          className={[
            'pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-72',
            '-translate-x-1/2 translate-y-1 scale-95 overflow-hidden rounded-xl',
            'border border-slate-200 bg-white text-left shadow-xl ring-1 ring-black/5',
            'opacity-0 transition duration-150 ease-out',
            'group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100',
            'group-focus-within:translate-y-0 group-focus-within:scale-100 group-focus-within:opacity-100',
          ].join(' ')}
        >
          <span
            className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-l border-t border-slate-200 bg-white"
            aria-hidden
          />
          <span
            className={[
              'relative flex items-center gap-1.5 border-b px-3 py-1.5',
              tone.badge,
            ].join(' ')}
          >
            <span className={['h-1.5 w-1.5 shrink-0 rounded-full', tone.dot].join(' ')} aria-hidden />
            <span className="text-[11px] font-bold uppercase tracking-wide">
              {presentation.label}
            </span>
          </span>
          <span className="relative block px-3 py-2 text-xs leading-snug text-slate-700">
            <span className="font-semibold text-slate-900">Why: </span>
            {presentation.message}
          </span>
        </span>
      ) : null}
    </span>
  );
};

export default SreCaseStatusBadge;
